/**
 * Подпорция 1.4 (2026-05-25): Inventory + Sale API client.
 */
import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';


export type AssetKind = 'FIXED_ASSET' | 'INTANGIBLE' | 'LOW_VALUE';
export type LocationKind = 'DEPARTMENT' | 'EMPLOYEE' | 'ORGANIZATION';
export type ActualStatus = 'FOUND' | 'MISSING' | 'FOUND_WRONG_LOCATION';


// ==================== Inventory ====================

export interface InventoryItem {
  id?: string;
  line_number: number;
  asset_kind: AssetKind;
  fixed_asset_id?: string | null;
  intangible_asset_id?: string | null;
  low_value_asset_id?: string | null;
  expected_department_id?: string | null;
  expected_employee_id?: string | null;
  actual_status: ActualStatus;
  actual_department_id?: string | null;
  actual_employee_id?: string | null;
  primary_cost_snapshot?: string | null;
  accumulated_depreciation_snapshot?: string | null;
  book_value_snapshot?: string | null;
  item_note?: string | null;
}

export interface FixedAssetInventory {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  location_kind: LocationKind;
  department_id: string | null;
  employee_id: string | null;
  inventory_period_start: string;
  inventory_period_end: string;
  inventory_reason: string | null;
  commission_chair_employee_id: string | null;
  commission_members_text: string | null;
  findings_summary: string | null;
  external_source?: string | null;
  items: InventoryItem[];
  created_at: string;
  updated_at: string;
  // Block 5.3c; 2026-08-31 (0451): статусний цикл DRAFT → IN_PROGRESS → POSTED.
  state?: CancellableState | 'IN_PROGRESS';
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface FixedAssetInventoryCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  location_kind: LocationKind;
  department_id?: string | null;
  employee_id?: string | null;
  inventory_period_start: string;
  inventory_period_end: string;
  inventory_reason?: string | null;
  commission_chair_employee_id?: string | null;
  commission_members_text?: string | null;
  findings_summary?: string | null;
  comment?: string | null;
  items: InventoryItem[];
}

export interface ExpectedItemPreview {
  asset_kind: AssetKind;
  asset_id: string;
  asset_code: string;
  asset_name: string;
  expected_department_id: string | null;
  expected_employee_id: string | null;
  primary_cost: string;
  accumulated_depreciation: string;
  book_value: string;
}

export interface ExpectedItemsResponse {
  items: ExpectedItemPreview[];
  total: number;
}


const INV_BASE = '/documents/fixed-asset-inventory';

export const inventory = {
  list: (params?: { organization_id?: string; posted?: boolean; date_from?: string; date_to?: string; limit?: number }) =>
    api.get<Page<FixedAssetInventory>>(INV_BASE, { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<FixedAssetInventory>(`${INV_BASE}/${id}`).then((r) => r.data),
  update: (id: string, p: Partial<FixedAssetInventoryCreate>) =>
    api.patch<FixedAssetInventory>(`${INV_BASE}/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`${INV_BASE}/${id}`).then(() => undefined),
  create: (p: FixedAssetInventoryCreate) =>
    api.post<FixedAssetInventory>(INV_BASE, p).then((r) => r.data),
  post: (id: string) =>
    api.post<FixedAssetInventory>(`${INV_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<FixedAssetInventory>(`${INV_BASE}/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`${INV_BASE}/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
  expectedItems: (params: {
    organization_id: string;
    location_kind: LocationKind;
    department_id?: string;
    employee_id?: string;
  }) =>
    api.get<ExpectedItemsResponse>(`${INV_BASE}/expected-items`, { params })
      .then((r) => r.data),
  printViewUrl: (id: string) => `/api/v1${INV_BASE}/${id}/print-view`,
  /** Статус: DRAFT ↔ IN_PROGRESS (опис у комісії), дзеркало складської. */
  setState: (id: string, state: 'DRAFT' | 'IN_PROGRESS') =>
    api.post<FixedAssetInventory>(`${INV_BASE}/${id}/set-state`, { state }).then((r) => r.data),
};


// ==================== Sale ====================

export interface SaleItem {
  id?: string;
  line_number: number;
  fixed_asset_id: string;
  primary_cost_snapshot?: string;
  accumulated_depreciation_snapshot?: string;
  book_value_snapshot?: string;
  sale_price_without_vat: string;
  vat_rate_percent?: string;
  vat_amount_override?: string | null;
  vat_amount?: string;
  sale_price_with_vat?: string;
  item_note?: string | null;
}

export interface FixedAssetSale {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  counterparty_id: string | null;
  external_buyer_name: string | null;
  external_buyer_tax_id: string | null;
  agreement_text: string | null;
  generated_tax_invoice_id: string | null;
  generated_tax_invoice_at: string | null;
  external_source?: string | null;
  items: SaleItem[];
  created_at: string;
  updated_at: string;
  // Block 5.3c:
  state?: CancellableState;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface FixedAssetSaleCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  counterparty_id?: string | null;
  external_buyer_name?: string | null;
  external_buyer_tax_id?: string | null;
  agreement_text?: string | null;
  comment?: string | null;
  items: Array<{
    line_number: number;
    fixed_asset_id: string;
    sale_price_without_vat: string;
    vat_rate_percent?: string;
    vat_amount_override?: string | null;
    item_note?: string | null;
  }>;
}

export interface VatPreviewRequest {
  sale_price_without_vat: string;
  vat_rate_percent?: string;
  vat_amount_override?: string | null;
}

export interface VatPreviewResponse {
  sale_price_without_vat: string;
  vat_rate_percent: string;
  vat_amount_calculated: string;
  vat_amount_used: string;
  sale_price_with_vat: string;
  is_override_active: boolean;
}


const SALE_BASE = '/documents/fixed-asset-sale';

export const sale = {
  list: (params?: { organization_id?: string; counterparty_id?: string;
                    posted?: boolean; limit?: number }) =>
    api.get<Page<FixedAssetSale>>(SALE_BASE, { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<FixedAssetSale>(`${SALE_BASE}/${id}`).then((r) => r.data),
  create: (p: FixedAssetSaleCreate) =>
    api.post<FixedAssetSale>(SALE_BASE, p).then((r) => r.data),
  update: (id: string, p: Partial<FixedAssetSaleCreate>) =>
    api.patch<FixedAssetSale>(`${SALE_BASE}/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`${SALE_BASE}/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<FixedAssetSale>(`${SALE_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<FixedAssetSale>(`${SALE_BASE}/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`${SALE_BASE}/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
  vatPreview: (p: VatPreviewRequest) =>
    api.post<VatPreviewResponse>(`${SALE_BASE}/vat-preview`, p).then((r) => r.data),
  printViewUrl: (id: string) => `/api/v1${SALE_BASE}/${id}/print-view`,
};
